import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, Star, Eye, Scale, Check as CheckIcon } from "lucide-react";
import { toast } from "sonner";
import { useWishlist } from "@/hooks/useWishlist";
import { useCompare } from "@/hooks/useCompare";
import { useSibaScoreConfig, tierForScore } from "@/hooks/useSibaScoreRanges";
import { useCurrency } from "@/contexts/CurrencyContext";
import type { Product } from "@/lib/types";
import { productImage } from "@/lib/productImage";
import { ecom } from "@/lib/analytics";
import { MiniScoreArc } from "@/components/shared/MiniScoreArc";
import { SibaScoreBadge } from "@/components/shared/SibaScoreBadge";
import { ProductScoreGauge } from "@/components/shared/ProductScoreGauge";
import { SmartImage } from "@/components/shared/SmartImage";
import { useSamplePool } from "@/lib/sampleImages";
import { VerifiedSellerBadge } from "@/components/shop/VerifiedSellerBadge";
import { QuickViewModal } from "@/components/shop/QuickViewModal";

// Tier label + color for a product's SibaScore come from the LIVE admin
// config (sibascore_ranges collection) — never hardcoded. The `useTierMeta`
// hook is a small closure over the live ranges so grid cards read the same
// tier that the gauge on the PDP shows.
const _tint = (hex: string, alpha = 0.14) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec((hex || "").replace("#", ""));
  if (!m) return `rgba(0,0,0,${alpha})`;
  const r = parseInt(m[1], 16), g = parseInt(m[2], 16), b = parseInt(m[3], 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

export function useTierMeta(score?: number | null) {
  const { ranges } = useSibaScoreConfig();
  if (score == null) return { label: null as string | null, color: "#cbd5e1", tint: "rgba(203,213,225,0.2)", ranges };
  const tier = tierForScore(score, ranges);
  return { label: tier.label, color: tier.color, tint: _tint(tier.color, 0.14), ranges };
}

// Heart button — wraps the wishlist toggle mutation. Sits absolutely on the
// product card so it never intercepts the outer <Link>. Anonymous users get
// a one-off toast pointing to /login. `position` picks the corner (default
// bottom-right on the /shop grid so the top-left corner stays reserved for
// the `% OFF` merchandising badge).
type CardCorner = "top-left" | "top-right" | "bottom-left" | "bottom-right";
const CORNER_POS: Record<CardCorner, string> = {
  "top-left": "top-2 left-2",
  "top-right": "top-2 right-2",
  "bottom-left": "bottom-2 left-2",
  "bottom-right": "bottom-2 right-2",
};

export const WishlistHeart: React.FC<{
  productId: string;
  slug?: string;
  testIdSuffix?: string;
  position?: CardCorner;
}> = ({ productId, slug, testIdSuffix, position = "bottom-right" }) => {
  const nav = useNavigate();
  const { isSaved, toggle } = useWishlist();
  const saved = isSaved(productId);
  const suffix = testIdSuffix || slug || productId;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const res = toggle(productId);
        if (res.requiresAuth) {
          toast("Sign in to save products to your wishlist", {
            action: { label: "Sign in", onClick: () => nav("/login?next=/wishlist") },
          });
          return;
        }
        toast(saved ? "Removed from wishlist" : "Saved to your wishlist ♥", {
          action: !saved ? { label: "View", onClick: () => nav("/wishlist") } : undefined,
          duration: 2000,
        });
      }}
      aria-pressed={saved}
      aria-label={saved ? "Remove from wishlist" : "Add to wishlist"}
      data-testid={`shop-card-heart-${suffix}`}
      className={`absolute z-10 ${CORNER_POS[position]} h-9 w-9 rounded-full flex items-center justify-center bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-700 hover:scale-105 active:scale-95 transition-all shadow-sm ${
        saved ? "text-rose-500" : "text-slate-500 dark:text-slate-400 hover:text-rose-500"
      }`}
    >
      <Heart className={`h-4 w-4 ${saved ? "fill-rose-500" : ""}`} strokeWidth={2} />
    </button>
  );
};

// Compare toggle button — icon-only, sits next to the heart in the
// bottom-right corner. Reveals on card hover on desktop, always visible
// once toggled on (so users can find it again to remove). The old
// top-left placement collided with the `% OFF` merchandising chip.
export const CompareToggle: React.FC<{
  productId: string;
  slug?: string;
  testIdSuffix?: string;
  position?: CardCorner;
}> = ({ productId, slug, testIdSuffix, position = "bottom-right" }) => {
  const { isCompared, toggle, count, max } = useCompare();
  const active = isCompared(productId);
  const suffix = testIdSuffix || slug || productId;
  // Offset one slot to the LEFT of the heart in the bottom-right stack.
  const stackOffset =
    position === "bottom-right" ? "bottom-2 right-12"
      : position === "top-right" ? "top-2 right-12"
        : position === "bottom-left" ? "bottom-2 left-12"
          : "top-2 left-12";
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const res = toggle(productId);
        if (res === "full") {
          toast(`Compare tray is full (${max} max). Remove one first.`, { duration: 2400 });
        } else if (res === "added") {
          toast(`Added to Compare (${count + 1}/${max})`, { duration: 1600 });
        }
      }}
      aria-pressed={active}
      aria-label={active ? "Remove from compare" : "Add to compare"}
      title={active ? "Remove from compare" : "Add to compare"}
      data-testid={`shop-card-compare-${suffix}`}
      className={`absolute z-10 ${stackOffset} h-9 w-9 rounded-full flex items-center justify-center border shadow-sm transition-all hover:scale-105 active:scale-95 ${
        active
          ? "bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-slate-900 dark:border-white opacity-100"
          : "bg-white/95 dark:bg-slate-900/95 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:text-slate-900 dark:hover:text-white opacity-0 group-hover:opacity-100 focus:opacity-100 focus-visible:opacity-100"
      }`}
    >
      {active ? <CheckIcon className="h-4 w-4" strokeWidth={2.5} /> : <Scale className="h-4 w-4" strokeWidth={2} />}
    </button>
  );
};

export const PremiumProductCard: React.FC<{ p: Product; listName?: string; index?: number }> = ({ p, listName, index }) => {
  const { formatPrice } = useCurrency();
  useSamplePool(); // Re-render when admin uploads land — makes uploaded photos visible without a page reload.
  const [quickOpen, setQuickOpen] = React.useState(false);
  const discount = p.compare_at_price && p.price && p.compare_at_price > p.price
    ? Math.round((1 - p.price / p.compare_at_price) * 100) : null;
  return (
    <>
      <Link
        to={`/shop/p/${p.slug}`}
        onClick={() => ecom.selectItem(listName || "shop", {
          item_id: p.product_id, item_name: p.title, item_brand: p.brand || undefined,
          item_category: p.category || undefined, price: p.price ?? undefined, currency: p.currency || "INR", index,
        })}
        data-testid={`shop-card-${p.slug}`}
        className="group relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-slate-900 dark:hover:border-slate-100 hover:shadow-md transition-all overflow-hidden flex flex-col"
      >
        <div className="aspect-square bg-slate-50 dark:bg-slate-800 relative overflow-hidden">
          <SmartImage
            src={productImage(p as any, 600)}
            alt={p.title}
            wrapperClassName="absolute inset-0"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
            testId={`shop-card-image-${p.slug}`}
          />
          {/* Top-left: % OFF merchandising chip (highest priority signal). */}
          {discount != null && (
            <span
              className="absolute top-2 left-2 z-10 bg-orange-500 text-white text-[10px] font-bold px-2 py-1 font-display shadow-sm"
              data-testid={`shop-card-discount-${p.slug}`}
            >
              {discount}% OFF
            </span>
          )}
          {/* Top-right: SibaScore trust badge (second highest signal). */}
          {p.product_score != null && (
            <span
              className="absolute top-2 right-2 z-10"
              data-testid={`shop-card-sibascore-${p.slug}`}
            >
              <SibaScoreBadge score={p.product_score} size="sm" />
            </span>
          )}
          {/* Bottom-left: Verified seller badge — trust chrome, always on. */}
          {p.verified_brand && (
            <span className="absolute bottom-2 left-2 z-10" data-testid={`shop-card-verified-${p.slug}`}>
              <VerifiedSellerBadge verified size="xs" showLabel={false} />
            </span>
          )}
          {/* Bottom-right: user actions — Heart (always) + Compare (hover). */}
          <CompareToggle productId={p.slug || p.product_id} slug={p.slug || undefined} position="bottom-right" />
          <WishlistHeart productId={p.product_id} slug={p.slug || undefined} position="bottom-right" />
          {/* Hover Quick View — opens an inline modal instead of navigating,
              preserving the shopper's place in the browse grid. */}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setQuickOpen(true);
            }}
            aria-label="Quick view"
            data-testid={`shop-card-quickview-${p.slug}`}
            className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
          >
            <span className="inline-flex items-center gap-1.5 bg-white/95 dark:bg-slate-900/95 text-slate-900 dark:text-white text-xs font-semibold px-3 py-2 shadow-lg border border-slate-200 dark:border-slate-700 rounded-full">
              <Eye className="h-3.5 w-3.5" /> Quick view
            </span>
          </button>
        </div>
        <div className="p-4 flex-1 flex flex-col border-t border-slate-100 dark:border-slate-800">
          <div className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wider font-display font-semibold flex items-center justify-between gap-2">
            <span className="truncate">{p.brand || "Unbranded"}</span>
            {(p as any).review_count ? (
              <span className="inline-flex items-center gap-0.5 text-slate-500 dark:text-slate-400 font-mono normal-case tracking-normal" data-testid={`shop-card-reviews-${p.slug}`}>
                <Star className="h-2.5 w-2.5 fill-amber-400 text-amber-400" />
                {(p as any).review_count}
              </span>
            ) : null}
          </div>
          <div className="font-display font-semibold text-sm mt-1 line-clamp-2 min-h-[2.5rem] group-hover:text-orange-500 transition-colors text-slate-900 dark:text-white" data-testid={`shop-card-title-${p.slug}`}>{p.title}</div>
          {p.product_score != null && (
            <div className="mt-3 flex items-center justify-center gap-2" data-testid={`shop-card-score-${p.slug}`}>
              <MiniScoreArc score={p.product_score} size={80} />
            </div>
          )}
          <div className="mt-auto pt-3 flex items-baseline gap-2 justify-center tabular-nums">
            <div className="font-display font-bold text-lg text-slate-900 dark:text-white">{formatPrice(p.price || 0)}</div>
            {p.compare_at_price && p.compare_at_price > (p.price || 0) && (
              <div className="text-xs text-slate-400 dark:text-slate-500 line-through font-display">{formatPrice(p.compare_at_price)}</div>
            )}
          </div>
        </div>
      </Link>
      <QuickViewModal product={p} open={quickOpen} onClose={() => setQuickOpen(false)} />
    </>
  );
};

// Full-width list row variant for the /shop grid/list toggle.
export const ProductListRow: React.FC<{ p: Product; listName?: string; index?: number }> = ({ p, listName, index }) => {
  const { formatPrice } = useCurrency();
  useSamplePool();
  return (
    <Link
      to={`/shop/p/${p.slug}`}
      onClick={() => ecom.selectItem(listName || "shop", {
        item_id: p.product_id, item_name: p.title, item_brand: p.brand || undefined,
        item_category: p.category || undefined, price: p.price ?? undefined, currency: p.currency || "INR", index,
      })}
      data-testid={`shop-list-${p.slug}`}
      className="group flex items-stretch gap-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-slate-900 dark:hover:border-slate-100 hover:shadow-sm transition-all overflow-hidden min-h-[144px] sm:min-h-[176px]"
    >
      <div className="w-32 sm:w-44 aspect-square bg-slate-50 dark:bg-slate-800 shrink-0 relative overflow-hidden">
        <WishlistHeart productId={p.product_id} slug={p.slug || undefined} testIdSuffix={`list-${p.slug || p.product_id}`} />
        <SmartImage
          src={productImage(p as any, 600)}
          alt={p.title}
          wrapperClassName="absolute inset-0"
          className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
          testId={`shop-list-image-${p.slug}`}
        />
        {p.product_score != null && (
          <span className="absolute top-2 right-2" data-testid={`shop-list-sibascore-${p.slug}`}>
            <SibaScoreBadge score={p.product_score} size="xs" showLabel={false} />
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0 py-3 sm:py-4 pr-4 flex flex-col">
        <div className="flex items-center gap-2 min-w-0">
          <div className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wider font-display font-semibold truncate">{p.brand || "Unbranded"}</div>
          {p.verified_brand && <VerifiedSellerBadge verified size="xs" testIdSuffix={`list-${p.slug || p.product_id}`} />}
        </div>
        <div className="font-display font-semibold text-base sm:text-lg text-slate-900 dark:text-white mt-1 line-clamp-2 group-hover:text-orange-500 transition-colors">
          {p.title}
        </div>
        {p.short_description && (
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2 hidden sm:block">{p.short_description}</p>
        )}
        <div className="mt-auto pt-3 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-baseline gap-2 tabular-nums">
            <div className="font-display font-bold text-lg sm:text-xl text-slate-900 dark:text-white">{formatPrice(p.price || 0)}</div>
            {p.compare_at_price && p.compare_at_price > (p.price || 0) && (
              <div className="text-xs text-slate-400 dark:text-slate-500 line-through font-display">{formatPrice(p.compare_at_price)}</div>
            )}
          </div>
          {p.product_score != null && (
            <div className="flex items-center gap-2 shrink-0">
              <MiniScoreArc score={p.product_score} size={64} />
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

// Legacy card variant — kept for other tabs that still use the older rounded styling.
export const ProductCard: React.FC<{ p: Product; listName?: string; index?: number }> = ({ p, listName, index }) => {
  const { formatPrice } = useCurrency();
  useSamplePool();
  return (
    <Link
      to={`/shop/p/${p.slug}`}
      onClick={() => ecom.selectItem(listName || "shop", {
        item_id: p.product_id, item_name: p.title, item_brand: p.brand || undefined,
        item_category: p.category || undefined, price: p.price ?? undefined, currency: p.currency || "INR", index,
      })}
      data-testid={`shop-card-${p.slug}`}
      className="group bg-white border border-[color:var(--sg-border)] hover:border-[color:var(--sb-saffron)]/50 hover:shadow-lg transition-all overflow-hidden flex flex-col"
      style={{ borderRadius: "0.5em" }}
    >
      <div className="aspect-square bg-[color:var(--sg-muted-bg)] relative overflow-hidden">
        <SmartImage
          src={productImage(p as any, 600)}
          alt={p.title}
          wrapperClassName="absolute inset-0"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute bottom-2 right-2">
          <ProductScoreGauge score={p.product_score ?? null} band={p.score_band || undefined} size="sm" />
        </div>
        {p.compare_at_price && p.price && p.compare_at_price > p.price && (
          <span className="absolute top-2 left-2 sg-chip bg-[color:var(--sb-saffron)] text-white border-transparent text-[10px]">
            {Math.round((1 - p.price / p.compare_at_price) * 100)}% OFF
          </span>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="text-[10px] text-[color:var(--sg-muted)] uppercase tracking-wider">{p.brand}</div>
        <div className="font-semibold text-sm mt-1 line-clamp-2 group-hover:text-[color:var(--sb-saffron)]">{p.title}</div>
        <div className="mt-auto pt-3 flex items-baseline gap-2">
          <div className="font-display font-bold text-lg">{formatPrice(p.price || 0)}</div>
          {p.compare_at_price && p.compare_at_price > (p.price || 0) && (
            <div className="text-xs text-[color:var(--sg-muted)] line-through">{formatPrice(p.compare_at_price)}</div>
          )}
        </div>
      </div>
    </Link>
  );
};
